/**
 * Custom Analytics Events Bridge
 * Publishes custom events to Shopify Analytics for tracking via web pixels
 *
 * Events tracked:
 * - added_to_wishlist: Product added to wishlist (in-house system)
 * - search:results-loaded: Search results loaded (from app proxy)
 * - search:result-clicked: Product clicked from search results (from app proxy)
 * - search:zero-results: Search returned no results (from app proxy)
 * - complete_registration: Klaviyo/Attentive popup signup completed (→ Stape/Meta CAPI)
 * - b2b_lead: B2B form submitted on art-for-business page (→ Stape/Meta CAPI)
 * - fy_identify: Broadcasts stored visitor identity to the pixel on every page
 *
 * Also writes acquisition_* custom attributes to Attentive on signup, so a
 * subscriber can be segmented by the funnel that acquired them (Hero vs DPA).
 */

(function () {
  const canPublish =
    window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function';

  if (!canPublish) {
    console.warn('[fy] Shopify.analytics.publish not available');
    return;
  }

  // small dedupe so we don't double fire on soft reloads
  const fired = new Set();
  const publishOnce = (key, name, payload) => {
    if (fired.has(key)) return;
    fired.add(key);
    Shopify.analytics.publish(name, payload || {});
  };

  // ---------- VISITOR IDENTITY STORE ----------
  // Persists email/phone captured from Klaviyo, Attentive, B2B, newsletter forms
  // so the pixel can enrich ALL events with user_data (even for non-logged-in visitors).
  // The pixel sandbox cannot read storefront cookies/localStorage, so we
  // broadcast via Shopify.analytics.publish('fy_identify') on every page load.
  const FY_IDENTITY_KEY = '_fy_identity';

  const IDENTITY_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function getStoredIdentity() {
    try {
      const raw = localStorage.getItem(FY_IDENTITY_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.updated_at && (Date.now() - parsed.updated_at) > IDENTITY_TTL_MS) {
        localStorage.removeItem(FY_IDENTITY_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function storeIdentity(data) {
    try {
      const existing = getStoredIdentity() || {};
      const merged = {
        email: data.email || existing.email || null,
        phone: data.phone || existing.phone || null,
        updated_at: Date.now()
      };
      localStorage.setItem(FY_IDENTITY_KEY, JSON.stringify(merged));

      // Publish immediately so the pixel's cachedVisitorIdentity is updated
      // in the current session (not just on next page load)
      if (merged.email || merged.phone) {
        Shopify.analytics.publish('fy_identify', {
          email: merged.email,
          phone: merged.phone
        });
      }

      // Push identity to Attentive so it can stitch browser activity to
      // subscriber profiles (works for any capture source: newsletter,
      // B2B form, cart persistence, etc.)
      if (window.attentive && window.attentive.analytics && typeof window.attentive.analytics.identify === 'function') {
        var identifyArgs = {};
        if (merged.email) identifyArgs.email = merged.email;
        if (merged.phone) identifyArgs.phone = merged.phone;
        window.attentive.analytics.identify(identifyArgs);
      }

      return merged;
    } catch (e) { return null; }
  }

  // Broadcast stored identity to the pixel on page load
  (function publishIdentity() {
    var identity = getStoredIdentity();
    if (identity && (identity.email || identity.phone)) {
      Shopify.analytics.publish('fy_identify', {
        email: identity.email,
        phone: identity.phone
      });
    }
  })();

  // ---------- SESSION ACQUISITION → ATTENTIVE CUSTOM ATTRIBUTES ----------
  // Attentive gets no traffic source from the Shopify integration, and Recart —
  // which captures GB and US signups since Aug 2026 — does not pass one either.
  // So the funnel that acquired a subscriber is unknowable inside Attentive
  // unless we write it there ourselves, which is what this block exists to do.
  //
  // Entry UTMs are stashed at session start because the popup usually fires
  // later than the landing page: ~13% of signups from a paid landing happen
  // after the visitor has navigated off it, by which point the campaign is gone
  // from the URL. First write wins, mirroring PostHog's entry_utm_* semantics.
  const FY_ACQUISITION_KEY = '_fy_acquisition';

  // Meta campaign names are pipe-delimited with the program first and a 6-digit
  // launch code in the middle:
  //   Art-Heroes | ASC | CBO | Prospecting | UK | 260611 | MaxVolume | HERO
  //   DPA-Testing | ASC | CBO | Prospecting | UK | 260427 | MaxVolume | NEW
  // The program prefix is the durable part. Matching on it rather than on a list
  // of codes means a new Hero feeder (a new code every few weeks) is labelled
  // correctly the day it launches, with no theme change and no Attentive edit.
  // The roster those codes come from is fy-dw/insights/hero_funnel/_segments.py.
  function classifyProgram(program) {
    const p = (program || '').toLowerCase();
    if (p.startsWith('art-heroes')) return 'hero';   // Art-Heroes + Art-Heroes-Testing
    if (p.startsWith('dpa')) return 'dpa';           // DPA-Testing/-Retargeting/-Remarketing
    return null;
  }

  // Channels we own. A click from one of these is not an acquisition — the
  // visitor was already on the list — so a session that starts there gets no
  // acquisition label at all rather than a wrong one. Attentive and Recart tag
  // their own links (utm_source=attentive, campaigns like 'Sunday_300826' or
  // 'Journey Browse Abandoner Email 3'), which look identical in shape to a paid
  // campaign name and would otherwise fall through to 'paid-other'.
  const OWNED_SOURCES = ['attentive', 'klaviyo', 'recart', 'email', 'sms', 'push'];

  // Mediums that actually denote paid traffic. Used only for the residual case:
  // a named campaign we cannot classify is labelled 'paid-other' ONLY when the
  // medium says it was paid. Without this, anything carrying a utm_campaign —
  // an affiliate link, a partner newsletter, a QR code — is asserted to be an ad.
  const PAID_MEDIUMS = ['paid', 'cpc', 'ppc', 'paidsocial', 'paid_social', 'paid-social', 'display'];

  function readAcquisitionFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      // URLSearchParams decodes %7C, so both the raw and encoded campaign
      // spellings we see in the wild arrive here as '|'.
      const campaign = (params.get('utm_campaign') || '').trim();
      const source = (params.get('utm_source') || '').trim();
      const medium = (params.get('utm_medium') || '').trim();
      if (!campaign && !source) return null;

      // An ad that shipped with an unresolved macro tells us nothing; recording
      // '{{campaign.name}}' as a program would be worse than recording nothing.
      if (campaign.includes('{{') || source.includes('{{') || medium.includes('{{')) return null;

      if (OWNED_SOURCES.indexOf(source.toLowerCase()) !== -1) return null;

      const program = campaign.split('|')[0].trim();
      const acq = {
        acquisition_funnel: classifyProgram(program),
        acquisition_program: program || null,
        acquisition_campaign_code: (campaign.match(/\b(\d{6})\b/) || [])[1] || null,
        acquisition_source: source || null,
        acquisition_medium: medium || null
      };
      // A campaign we can name but not classify is only called paid when the
      // medium says so. Otherwise the factual fields are still recorded and the
      // funnel is simply left unset — unknown, not guessed.
      if (!acq.acquisition_funnel && acq.acquisition_program &&
          PAID_MEDIUMS.indexOf(medium.toLowerCase()) !== -1) {
        acq.acquisition_funnel = 'paid-other';
      }
      return acq.acquisition_program ? acq : null;
    } catch (e) { return null; }
  }

  function getAcquisition() {
    try {
      const raw = sessionStorage.getItem(FY_ACQUISITION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  (function storeAcquisition() {
    try {
      if (sessionStorage.getItem(FY_ACQUISITION_KEY)) return; // first touch wins
      const acq = readAcquisitionFromUrl();
      if (acq) sessionStorage.setItem(FY_ACQUISITION_KEY, JSON.stringify(acq));
    } catch (e) { /* private mode — attribution is best-effort, never fatal */ }
  })();

  /**
   * Label a fresh subscriber with the funnel that acquired them, so Attentive
   * can segment on it (Segment builder → Custom Attribute → acquisition_funnel).
   *
   * Only sends when the session carries a campaign: absence of the attribute in
   * Attentive means "no campaign attribution captured", which is the truthful
   * reading — writing 'organic' would claim more than we know, since a dropped
   * UTM looks identical to a genuine direct visit.
   *
   * @param {{ email?: string|null, phone?: string|null }} user
   */
  function sendAcquisitionAttributes(user) {
    const acq = getAcquisition();
    if (!acq) return;
    if (!user || (!user.email && !user.phone)) return;

    const properties = {};
    for (const key of Object.keys(acq)) {
      if (acq[key]) properties[key] = acq[key];
    }
    if (Object.keys(properties).length === 0) return;

    fetch('/apps/chat-agent/api/attentive-attributes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true, // the signup often precedes a navigation or a tab close
      body: JSON.stringify({ properties: properties, user: user }),
    }).catch(function (err) {
      console.error('[fy] Attentive acquisition attributes failed:', err.message);
    });
  }

  // ---------- IN-HOUSE WISHLIST → added_to_wishlist ----------
  function handleWishlistForStape(/** @type {CustomEvent} */ e) {
    const detail = e.detail || {};
    if (detail.event !== 'added_to_wishlist') return;

    // Dedupe: 'wishlist:analytics' and 'custom-analytics' can both fire for the
    // same action during the CDN cache transition (same guard the Klaviyo /
    // Attentive handlers use) — without this we'd publish two CAPI events.
    const dedupeKey = `stape_wishlist_${detail.product_handle}`;
    if (fired.has(dedupeKey)) return;
    fired.add(dedupeKey);

    // Pull the join key (sku = legacy_id) + price/currency off the
    // <wishlist-button> for this handle (every wishlist-button now carries them —
    // PDP via wishlist-button.liquid, cards via product-card-builder.js) so the
    // pixel can populate content_ids / value. Require [data-product-sku] so we
    // match a button that actually carries the key (vs nulling it out).
    const handle = detail.product_handle;
    const btn = handle
      ? document.querySelector(`wishlist-button[data-product-handle="${CSS.escape(handle)}"][data-product-sku]`)
      : null;
    const sku = (btn && btn.dataset.productSku) || null;
    const priceRaw = btn && btn.dataset.productPrice;
    const price = priceRaw != null && priceRaw !== '' ? parseFloat(priceRaw) : null;
    const currencyCode = (btn && btn.dataset.currency) || null;

    const productVariant = { product: { id: detail.product_id || null } };
    if (sku) productVariant.sku = sku;
    if (price != null && !isNaN(price)) {
      productVariant.price = { amount: price };
      if (currencyCode) productVariant.price.currencyCode = currencyCode;
    }

    Shopify.analytics.publish('added_to_wishlist', {
      productVariant,
      source: 'in-house',
      product_handle: detail.product_handle || null,
    });
  }
  // Listen for both: 'wishlist:analytics' is canonical, 'custom-analytics' is
  // the old name still served from CDN cache until wishlist-state.js cache expires
  document.addEventListener('wishlist:analytics', handleWishlistForStape);
  document.addEventListener('custom-analytics', handleWishlistForStape);

  // ---------- IN-HOUSE WISHLIST → Klaviyo "Wishlist" event ----------
  function handleWishlistForKlaviyo(/** @type {CustomEvent} */ e) {
    const detail = e.detail || {};
    if (detail.event !== 'added_to_wishlist') return;

    const handle = detail.product_handle;
    if (!handle) return;

    // Dedupe: both 'wishlist:analytics' and 'custom-analytics' can fire for the
    // same action during CDN cache transition. Reuse the module-level `fired` Set.
    const dedupeKey = `klaviyo_wishlist_${handle}`;
    if (fired.has(dedupeKey)) return;
    fired.add(dedupeKey);

    // Look up product data from the DOM element
    const btn = document.querySelector(`wishlist-button[data-product-handle="${CSS.escape(handle)}"]`);
    let imageURL = btn?.dataset.productImage || '';
    if (imageURL.startsWith('//')) imageURL = `https:${imageURL}`;
    imageURL = imageURL.replace(/[?&]q=100\b/, '');
    const name = btn?.dataset.productTitle || '';
    const productID = detail.product_id || btn?.dataset.productId || '';
    const url = `${window.location.origin}/products/${handle}`;

    const orientation = btn?.dataset.productOrientation || '';
    const payload = { ImageURL: imageURL, URL: url, ProductID: productID, Name: name, Orientation: orientation };

    // klaviyo is injected by the Klaviyo Shopify app embed
    const kl = window.klaviyo;
    if (kl && typeof kl.push === 'function') {
      kl.push(['track', 'Wishlist', payload]);
    }
  }
  document.addEventListener('wishlist:analytics', handleWishlistForKlaviyo);
  document.addEventListener('custom-analytics', handleWishlistForKlaviyo);

  // ---------- IN-HOUSE WISHLIST → Attentive "Added to Wishlist" event ----------
  // Attentive has no client-side custom event API, so we POST to the app proxy
  // which forwards to Attentive's server-side REST API.
  function handleWishlistForAttentive(/** @type {CustomEvent} */ e) {
    const detail = e.detail || {};
    if (detail.event !== 'added_to_wishlist') return;

    const handle = detail.product_handle;
    if (!handle) return;

    const dedupeKey = `attentive_wishlist_${handle}`;
    if (fired.has(dedupeKey)) return;
    fired.add(dedupeKey);

    const btn = document.querySelector(`wishlist-button[data-product-handle="${CSS.escape(handle)}"]`);
    let imageURL = btn?.dataset.productImage || '';
    if (imageURL.startsWith('//')) imageURL = `https:${imageURL}`;
    imageURL = imageURL.replace(/[?&]q=100\b/, '');
    const name = btn?.dataset.productTitle || '';
    const productID = detail.product_id || btn?.dataset.productId || '';
    const url = `${window.location.origin}/products/${handle}`;
    const orientation = btn?.dataset.productOrientation || '';

    const identity = getStoredIdentity();
    const user = {};
    if (identity?.email) user.email = identity.email;
    if (identity?.phone) user.phone = identity.phone;

    fetch('/apps/chat-agent/api/attentive-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        type: 'Added to Wishlist',
        properties: { ImageURL: imageURL, URL: url, ProductID: productID, Name: name, Orientation: orientation },
        user: user,
        externalEventId: crypto.randomUUID(),
      }),
    }).catch(function (err) { console.error('[fy] Attentive wishlist event failed:', err.message); });
  }
  document.addEventListener('wishlist:analytics', handleWishlistForAttentive);
  document.addEventListener('custom-analytics', handleWishlistForAttentive);

  // ---------- ATTENTIVE CREATIVE → complete_registration ----------
  // Attentive's creative runs in a cross-origin iframe; there is no completion
  // callback like Klaviyo's `klaviyoForms`. We publish complete_registration via
  // two paths sharing one dedupe (publishSignup fires at most once per signup):
  //
  //   PRIMARY — Attentive Data-Layer Push integration (enabled in the Attentive
  //   account). On capture the growth tag pushes to window.dataLayer:
  //     { event:'submitEmail', source:'Attentive', email } — email step
  //     { event:'submitSMS',   source:'Attentive' }         — SMS step (NO PII)
  //   Only submitEmail carries the email, and the push carries no phone value, so
  //   we publish immediately on submitEmail (no close/iframe heuristics, no webhook
  //   race). submitSMS just nudges the fallback poll, which returns the stored
  //   email+phone for SMS-first / SMS-only creatives.
  //
  //   FALLBACK — for visitors/markets where the data-layer integration isn't
  //   firing: set __attentive_client_user_id before Attentive loads
  //   (attentive-identity.liquid), detect when the creative is removed (closed) via
  //   MutationObserver, then poll the app proxy for a signup cached by the Attentive
  //   webhook receiver, keyed by client_user_id (echoed in external_identifiers).
  //
  // Close-detection note (why the previous attn.tv-src heuristic silently broke,
  // taking the only path down with it): Attentive's current ("2026-latest") growth
  // tag renders the creative as an <iframe id="attentive_creative"> inside a
  // <div id="attentive_overlay">, and (a) appends the iframe with an EMPTY src then
  // sets the creatives.attn.tv src asynchronously (an attribute change, NOT a
  // childList insertion), and (b) on close removes the overlay AND blanks the
  // iframe src (src='') in the same tick. So matching `attn.tv` in the iframe `src`
  // at childList-mutation time never matched — neither on add (src empty) nor on
  // remove (src blanked) — leaving the "present" flag permanently false. The
  // fallback therefore keys on Attentive's stable element IDs, keeping the
  // attn.tv-src check only for older/embedded creative variants.
  (function observeAttentiveCreative() {
    var CUID_KEY = '_fy_attn_cuid';
    var OVERLAY_ID = 'attentive_overlay';   // Attentive growth-tag overlay host
    var CREATIVE_ID = 'attentive_creative'; // Attentive growth-tag creative iframe
    var MAX_ATTEMPTS = 6;                    // ~15s budget — the webhook can lag the close
    var POLL_INTERVAL_MS = 2500;
    var creativePresent = false;
    var signupConsumed = false;
    var pollActive = false; // one in-flight poll chain at a time

    // Publish complete_registration once per signup. Shared by the data-layer
    // listener (primary) and the webhook-poll fallback so we never double-fire.
    function publishSignup(data) {
      if (signupConsumed) return;
      signupConsumed = true;

      storeIdentity({ email: data.email, phone: data.phone });

      // EU still signs up through Attentive's own units, so it needs the same
      // acquisition label as the Recart path below.
      sendAcquisitionAttributes({ email: data.email || null, phone: data.phone || null });

      Shopify.analytics.publish('complete_registration', {
        source: 'attentive',
        email: data.email || null,
        phone: data.phone || null,
        form_id: data.form_id || 'attentive',
      });

      document.dispatchEvent(new CustomEvent('attentive:signup-completed', {
        detail: {
          source: 'popup',
          email: data.email || null,
          phone: data.phone || null,
          form_id: data.form_id || 'attentive',
        }
      }));
    }

    // ---- PRIMARY: Attentive Data-Layer Push integration ----
    // submitEmail carries the email → publish immediately. submitSMS carries no
    // value → nudge the fallback poll (which has email+phone from the webhook).
    function handleDataLayerEvent(obj) {
      if (!obj || obj.source !== 'Attentive' || signupConsumed) return;
      if (obj.event === 'submitEmail' && obj.email) {
        publishSignup({ email: obj.email, form_id: obj.creative_id || 'attentive' });
      } else if (obj.event === 'submitSMS') {
        onCreativeClosed();
      }
    }

    window.dataLayer = window.dataLayer || [];
    // Catch anything already queued before this wrapper installed…
    for (var d = 0; d < window.dataLayer.length; d++) {
      try { handleDataLayerEvent(window.dataLayer[d]); } catch (e) {}
    }
    // …and observe future pushes (call through so GTM still receives them).
    // Wrap the *current* push so OUR handler runs first, then delegates.
    function wrapDataLayerPush(underlying) {
      return function () {
        for (var a = 0; a < arguments.length; a++) {
          try { handleDataLayerEvent(arguments[a]); } catch (e) {}
        }
        return underlying.apply(this, arguments);
      };
    }
    // Install via an accessor so we survive GTM's load order: when gtm.js later
    // (re)assigns dataLayer.push with its own proxy, our setter re-wraps it
    // instead of being silently clobbered (which would drop the primary path and
    // leave only the close→poll fallback). Falls back to a plain wrap if the
    // accessor can't be defined.
    try {
      var wrappedPush = wrapDataLayerPush(window.dataLayer.push);
      Object.defineProperty(window.dataLayer, 'push', {
        configurable: true,
        enumerable: false,
        get: function () { return wrappedPush; },
        set: function (next) { wrappedPush = wrapDataLayerPush(next); },
      });
    } catch (e) {
      window.dataLayer.push = wrapDataLayerPush(window.dataLayer.push);
    }

    // True if `node` is (or contains) the Attentive creative overlay/iframe.
    // Stable-ID match is primary; the attn.tv-src check is a fallback only.
    function isAttentiveCreative(node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.id === OVERLAY_ID || node.id === CREATIVE_ID) return true;
      if (node.tagName === 'IFRAME' && /attn\.tv/i.test(node.src || '')) return true;
      if (node.querySelector) {
        return !!node.querySelector('#' + OVERLAY_ID + ',#' + CREATIVE_ID + ',iframe[src*="attn.tv"]');
      }
      return false;
    }

    function pollForSignup(cuid, attempt) {
      if (attempt > MAX_ATTEMPTS || signupConsumed) { pollActive = false; return; }

      fetch('/apps/chat-agent/api/attentive-signup?cuid=' + encodeURIComponent(cuid), {
        credentials: 'same-origin',
      })
        .then(function (r) {
          if (!r.ok) {
            // Retry on transient server errors; give up on client errors (401, 404)
            if (r.status >= 500) setTimeout(function () { pollForSignup(cuid, attempt + 1); }, POLL_INTERVAL_MS);
            else pollActive = false;
            return;
          }
          return r.json();
        })
        .then(function (data) {
          if (!data) return; // non-2xx handled above
          if (!data.found) {
            // Webhook may not have arrived yet — retry
            setTimeout(function () { pollForSignup(cuid, attempt + 1); }, POLL_INTERVAL_MS);
            return;
          }

          pollActive = false;
          publishSignup({
            email: data.email,
            phone: data.phone,
            form_id: data.creative_name || 'attentive',
          });
        })
        .catch(function (err) {
          pollActive = false;
          console.error('[fy] Attentive signup check failed:', err.message);
        });
    }

    // Kick off a single poll chain. Both the submitSMS data-layer signal and the
    // creative-close observer call this; the pollActive guard keeps them from
    // racing two concurrent chains (e.g. an SMS-only creative fires submitSMS then
    // closes ~200ms later).
    function onCreativeClosed() {
      if (signupConsumed || pollActive) return;
      var cuid = localStorage.getItem(CUID_KEY);
      if (!cuid) {
        console.log('[fy] Attentive creative closed, no client user ID');
        return;
      }
      pollActive = true;
      pollForSignup(cuid, 1);
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          if (isAttentiveCreative(m.addedNodes[j])) creativePresent = true;
        }
        for (var k = 0; k < m.removedNodes.length; k++) {
          if (isAttentiveCreative(m.removedNodes[k]) && creativePresent) {
            creativePresent = false;
            onCreativeClosed();
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();

  // ---------- RECART OPT-IN TOOL → complete_registration ----------
  // Recart publishes three CustomEvents on `window`; the payload is `event.detail`.
  //   recart:optin-tool:interaction           — { …ids, interaction }
  //   recart:optin-tool:email-captured        — { …ids, email }
  //   recart:optin-tool:phone-number-captured — { …ids, phoneNumber }
  // ids = optinToolId, optinToolName, optinToolExperimentId?, optinToolExperimentName?
  // https://recart-app.stoplight.io/docs/openapi/xz7vhcvtwprrz-listening-to-recart-opt-in-tool-events
  //
  // Two things differ from the Attentive block above, both in our favour. The
  // contract is documented, so there is no DOM scraping, no MutationObserver on
  // vendor element ids and no webhook reconciliation — this listener is the only
  // path, and it cannot silently go false the way the attn.tv-src heuristic did.
  //
  // And `interaction: 'impression'` is a genuinely new signal: no previous provider
  // emitted one, which is why every signup-rate denominator in the warehouse is a
  // ≥20s dwell proxy. With it, impressions-per-engaged-session is measurable, and
  // "the unit is not being served" separates from "the unit converts badly" — the
  // ambiguity that hid the 2026-07 US outage for a week behind a healthy-looking
  // per-impression rate.
  //
  // ⚠️ Event access is gated per Recart account. If they have not enabled it for
  // the shop, these events never fire and this block is inert — silently.
  (function observeRecartOptinTool() {
    const consumed = new Set(); // optinToolId → capture already reported

    const toolProps = (d) => ({
      form_id: d.optinToolId || 'recart',
      optin_tool_name: d.optinToolName || null,
      experiment_id: d.optinToolExperimentId || null,
      experiment_name: d.optinToolExperimentName || null,
    });

    // Fires once per opt-in tool per pageview, on whichever of email/phone lands
    // first. Deliberately immediate rather than buffered across steps like the
    // Klaviyo block below: buffering costs the event entirely when a visitor
    // converts and leaves inside the window (mobile Safari does not reliably fire
    // beforeunload), and the only thing gained is `has_phone` on an email→phone
    // flow, which is diagnostic. A later phone step still reaches storeIdentity,
    // so identity is complete even when the reported boolean is conservative.
    function reportCapture(detail, data) {
      const id = detail.optinToolId || 'recart';
      storeIdentity({ email: data.email, phone: data.phone });
      if (consumed.has(id)) return;
      consumed.add(id);

      // Inside the consumed guard so a two-step email→phone flow writes once.
      sendAcquisitionAttributes({ email: data.email || null, phone: data.phone || null });

      const props = toolProps(detail);

      Shopify.analytics.publish('complete_registration', {
        source: 'recart',
        email: data.email || null,
        phone: data.phone || null,
        form_id: props.form_id,
      });

      document.dispatchEvent(new CustomEvent('recart:signup-completed', {
        detail: Object.assign({
          source: 'popup',
          email: data.email || null,
          phone: data.phone || null,
        }, props),
      }));
    }

    window.addEventListener('recart:optin-tool:email-captured', (e) => {
      const d = e.detail || {};
      if (!d.email) return;
      reportCapture(d, { email: d.email });
    });

    window.addEventListener('recart:optin-tool:phone-number-captured', (e) => {
      const d = e.detail || {};
      if (!d.phoneNumber) return;
      reportCapture(d, { phone: d.phoneNumber });
    });

    // Impressions and clicks. Forwarded as-is; the PostHog listener owns dedupe
    // and naming, so this stays a pure translation of the vendor event.
    window.addEventListener('recart:optin-tool:interaction', (e) => {
      const d = e.detail || {};
      if (!d.interaction) return;
      document.dispatchEvent(new CustomEvent('recart:optin-interaction', {
        detail: Object.assign({ interaction: d.interaction }, toolProps(d)),
      }));
    });
  })();

  // ---------- KLAVIYO POPUP → complete_registration ----------
  // Klaviyo fires "klaviyoForms" on window for each form lifecycle step.
  // Multi-step forms fire "stepSubmit" per step (e.g. email then phone).
  // We accumulate data across steps and fire once on "close" or "redirectedToUrl".
  // See legacy: fy-core/lib/api/src-cljs/fy/web/app.cljs
  (function observeKlaviyoForms() {
    const formData = {}; // formId → { email, phone, formId }

    window.addEventListener('klaviyoForms', (e) => {
      const detail = e.detail || {};
      const formId = detail.formId || 'unknown';

      if (detail.type === 'stepSubmit') {
        const metadata = detail.metaData || {};
        const entry = formData[formId] || { formId: formId };

        // Accumulate across steps — email on step 1, phone on step 2, etc.
        entry.email = metadata.$email || metadata.email || entry.email || null;
        entry.phone = metadata.$phone_number || metadata.phone_number || entry.phone || null;

        formData[formId] = entry;

        // Fire immediately on first step that has data, so Meta gets the
        // event in the same session. If a later step adds phone, we fire again
        // with the full data — GTM can dedupe on formId if needed.
        if (entry.email || entry.phone) {
          storeIdentity({ email: entry.email, phone: entry.phone });

          Shopify.analytics.publish('complete_registration', {
            source: 'klaviyo',
            email: entry.email,
            phone: entry.phone,
            form_id: formId
          });

          document.dispatchEvent(new CustomEvent('klaviyo:signup-completed', {
            detail: {
              source: 'popup',
              email: entry.email,
              phone: entry.phone,
              form_id: formId
            }
          }));
        }
      }
    });
  })();

  // ---------- B2B FORM (ART FOR BUSINESS) → b2b_lead ----------
  // Shopify Forms app renders inside <shopify-forms-embed> web components with
  // shadow DOM. We cannot listen for submit events from outside, so instead we:
  //   1. Read form field values from the shadow DOM inputs on click of submit
  //   2. Watch data-current-step on [data-form-root] — it changes from "form"
  //      to a success step after a successful submission
  //   3. Fire the analytics event only when the step actually changes
  (function observeB2BForm() {
    if (!/\/pages\/art-for-business\b/.test(window.location.pathname)) return;

    function readFormData(shadowRoot) {
      return {
        email: (shadowRoot.querySelector('#email') || {}).value || null,
        phone: (shadowRoot.querySelector('#phone_number') || {}).value || null,
        name: (shadowRoot.querySelector('#first_name') || {}).value || null,
        company: (shadowRoot.querySelector('#company_name') || {}).value || null
      };
    }

    function handleSuccess(data) {
      storeIdentity({ email: data.email, phone: data.phone });

      var dedupeKey = 'b2b_' + (data.email || Date.now());
      publishOnce(dedupeKey, 'b2b_lead', {
        source: 'b2b_form',
        email: data.email,
        phone: data.phone,
        form_id: 'b2b-art-for-business'
      });

      document.dispatchEvent(new CustomEvent('b2b:form-submitted', {
        detail: {
          source: 'b2b_form',
          email: data.email,
          phone: data.phone,
          name: data.name,
          company: data.company,
          form_id: 'b2b-art-for-business'
        }
      }));
    }

    function observeEmbed(embed) {
      var shadow = embed.shadowRoot;
      if (!shadow) return;

      var root = shadow.querySelector('[data-form-root]');
      if (!root) return;

      // Snapshot field values whenever submit is clicked, before the DOM changes
      var pendingData = null;
      shadow.addEventListener('click', function (e) {
        if (e.target && e.target.getAttribute && e.target.getAttribute('data-testid') === 'btn-form-submit') {
          pendingData = readFormData(shadow);
        }
      });

      // Watch for data-current-step to change away from "form"
      var stepObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName !== 'data-current-step') continue;
          var newStep = root.getAttribute('data-current-step');
          if (newStep && newStep !== 'form') {
            // Form transitioned to success/thank-you step
            var data = pendingData || readFormData(shadow);
            pendingData = null;
            handleSuccess(data);
            stepObserver.disconnect();
            return;
          }
        }
      });

      stepObserver.observe(root, { attributes: true, attributeFilter: ['data-current-step'] });
    }

    // Shopify Forms embeds may already be in the DOM or load later
    document.querySelectorAll('shopify-forms-embed').forEach(observeEmbed);

    // Also watch for late-loading embeds
    var bodyObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.tagName === 'SHOPIFY-FORMS-EMBED') observeEmbed(node);
          if (node.querySelectorAll) {
            node.querySelectorAll('shopify-forms-embed').forEach(observeEmbed);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  })();

  // ---------- SEARCH APP PROXY EVENTS ----------

  // Search results loaded
  document.addEventListener('search:results-loaded', (e) => {
    const { query, result_count, page, execution_time_ms, product_ids, filters, sort } = e.detail || {};

    Shopify.analytics.publish('search_results_loaded', {
      query: query,
      result_count: result_count,
      page: page,
      execution_time_ms: execution_time_ms,
      product_ids: product_ids,
      filters: filters,
      sort: sort
    });
  });

  // Search result clicked
  document.addEventListener('search:result-clicked', (e) => {
    const { query, product_id, product_handle, position, page, result_count } = e.detail || {};

    Shopify.analytics.publish('search_result_clicked', {
      query: query,
      product_id: product_id,
      product_handle: product_handle,
      position: position,
      page: page,
      result_count: result_count
    });
  });

  // Zero results
  document.addEventListener('search:zero-results', (e) => {
    const { query, suggested_query, execution_time_ms } = e.detail || {};

    Shopify.analytics.publish('search_zero_results', {
      query: query,
      suggested_query: suggested_query,
      execution_time_ms: execution_time_ms
    });
  });

  // ---------- NEWSLETTER / EMAIL SIGNUP FORMS ----------
  // Shopify native email-signup forms do a full page POST. Use event delegation
  // so lazily-rendered forms (e.g. section loading on scroll) are also captured.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains('email-signup__form')) return;
    var input = form.querySelector('input[type="email"]');
    if (input && input.value) {
      storeIdentity({ email: input.value });
    }
  });
})();
